import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FiMenu, FiX } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ComicButton from '../ui/ComicButton';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const logoUrl = "https://static.wixstatic.com/media/9fff18_7f77218ff74e4a6a93c1ad8122a0c3d3~mv2.png/v1/fill/w_288,h_286,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/thumbnail%20(3).png";

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Line Up', path: '/lineup' },
    { name: 'Program', path: '/program' },
    { name: 'About', path: '/about' },
    { name: 'Merch', path: '/merch' },
    { name: 'FAQ', path: '/faq' },
  ];

  return (
    <nav className="sticky top-0 z-[100] bg-white border-b-4 border-black px-4 py-2 shadow-comic">
      {/* Decorative Top Bar */}
      <div className="absolute top-0 left-0 w-full h-2 bg-hcf-gradient"></div>
      
      <div className="max-w-7xl mx-auto flex justify-between items-center pt-2">
        <NavLink to="/" className="flex items-center gap-3 group">
          <div className="w-16 h-16 bg-white border-4 border-black shadow-comic-sm transform -rotate-3 group-hover:rotate-0 transition-transform flex items-center justify-center p-1 overflow-hidden">
            <img src={logoUrl} alt="HCF Logo" className="w-full h-full object-contain" />
          </div>
          <div className="hidden md:block">
            <span className="font-comic text-2xl uppercase tracking-tighter leading-none block text-black">Hastings</span>
            <span className="font-comic text-xl uppercase tracking-widest text-hcf-pink leading-none drop-shadow-[1px_1px_0_rgba(0,0,0,1)]">Comedy Festival</span>
          </div>
        </NavLink>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <NavLink 
              key={item.name} 
              to={item.path} 
              className={({ isActive }) => `
                font-comic text-xl uppercase tracking-wide transition-all hover:-translate-y-1
                ${isActive ? 'text-hcf-pink underline decoration-4 underline-offset-4 decoration-hcf-cyan' : 'text-black hover:text-hcf-pink'}
              `}
            >
              {item.name}
            </NavLink>
          ))}
          <ComicButton variant="primary" className="text-sm py-2 px-4 bg-hcf-yellow text-black hover:bg-black hover:text-white border-black">
            Buy Tickets
          </ComicButton>
        </div>

        {/* Mobile Toggle */}
        <button 
          className="md:hidden text-3xl p-2 border-2 border-black bg-white text-hcf-pink shadow-comic-sm active:translate-y-1 active:shadow-none transition-all relative z-[110]"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle Menu"
        >
          <SafeIcon icon={isOpen ? FiX : FiMenu} />
        </button>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop to ensure click-away and focus */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/40 z-[90] md:hidden"
            />
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              className="md:hidden fixed top-[84px] left-0 w-full bg-white border-b-4 border-black shadow-comic p-4 flex flex-col gap-2 z-[100]"
            >
              {navItems.map((item) => (
                <NavLink 
                  key={item.name} 
                  to={item.path} 
                  className={({ isActive }) => `
                    font-comic text-2xl uppercase text-center py-3 border-2 border-black transition-colors
                    ${isActive ? 'bg-hcf-pink text-white' : 'bg-white text-black hover:bg-hcf-yellow'}
                  `}
                >
                  {item.name}
                </NavLink>
              ))}
              <div className="mt-2 pt-2 border-t-2 border-dashed border-black">
                <ComicButton 
                  variant="primary" 
                  className="w-full text-xl bg-hcf-yellow text-black border-black"
                >
                  Buy Tickets
                </ComicButton>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;